import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';

import { ApiError, fetchJson, postJson } from '@/lib/api-client';

import { AuthContext, type AppRole, type AuthPhase, type AuthState, type AuthUser } from './auth-context';

/**
 * The SPA's entire authentication surface: one call to `GET /api/v1/me`.
 *
 * There is no MSAL in this bundle and there never will be (CONVENTIONS.md
 * §4.6 / A6). The FastAPI backend is the OIDC client — it runs the auth-code
 * exchange, validates the token, and hands the browser nothing but an opaque
 * httpOnly session cookie. So the frontend cannot inspect a token, cannot
 * refresh one, and cannot decide a role. It asks the server who it is talking
 * to and renders accordingly.
 *
 * Three outcomes, and the difference between the last two is the whole reason
 * this component is not four lines:
 *
 * - **200** — render the app.
 * - **401** — no session. Navigate to `/api/auth/login`, carrying the current
 *   path so a deep link into `/missions/M5?fn=FN-16` survives the round trip
 *   instead of dumping everyone on the overview.
 * - **403** — signed in, not entitled. Signing in again would loop them
 *   through a successful Entra login straight back into the same refusal, so
 *   this must NOT redirect. It explains who to ask.
 */

interface MeResponse {
  id: string;
  email: string;
  displayName: string;
  role: AppRole;
}

/**
 * Messages for the `?authError=` flags the BFF's callback bounces back with.
 * They are set on a top-level navigation, so the alternative is a raw JSON
 * error envelope rendered as a bare page — not something a leadership
 * audience can act on.
 */
const CALLBACK_MESSAGES: Record<string, string> = {
  denied: 'Sign-in was cancelled or refused by Microsoft. Try again.',
  forbidden:
    'Your JCORP account is not on the access list for this dashboard. Ask the Digital DCDO to add you.',
  expired: 'That sign-in attempt timed out. Try again.',
  exchange: 'Microsoft rejected the sign-in. Try again; if it persists, report it to Digital.',
  unreachable: 'Could not reach Microsoft to complete sign-in. Check your connection and retry.',
  invalid: 'The sign-in response could not be verified. Try again.',
};

/** Where to send the browser to start a sign-in, preserving the deep link. */
function loginUrl(): string {
  const here = `${window.location.pathname}${window.location.search}`;
  return `/api/auth/login?returnTo=${encodeURIComponent(here)}`;
}

/** The `authError` flag the callback may have just bounced back with. */
function callbackError(): string | null {
  return new URLSearchParams(window.location.search).get('authError');
}

export interface AuthProviderProps {
  children: ReactNode;
  /**
   * Test seam. When provided — including as `null` — no request is made and
   * the phase is settled immediately. Tests that render a page do not want a
   * network call, and tests that assert role gating need to choose the role.
   */
  user?: AuthUser | null;
}

export function AuthProvider({ children, user }: AuthProviderProps) {
  const seeded = user !== undefined;
  const [resolved, setResolved] = useState<AuthUser | null>(seeded ? user : null);
  const [phase, setPhase] = useState<AuthPhase>(seeded ? 'ready' : 'resolving');
  const [message, setMessage] = useState('');
  // StrictMode double-invokes effects in development. Without this the app
  // fires two /me requests and, on a 401, can start two navigations.
  const started = useRef(false);

  useEffect(() => {
    if (seeded || started.current) return;
    started.current = true;

    const flag = callbackError();
    if (flag !== null) {
      // We have just come back from a failed callback. Redirecting to /login
      // here is how a sign-in failure becomes an infinite loop between the
      // SPA and Microsoft, so stop and say what happened.
      setPhase(flag === 'forbidden' ? 'denied' : 'failed');
      setMessage(CALLBACK_MESSAGES[flag] ?? 'Sign-in did not complete. Try again.');
      return;
    }

    let cancelled = false;
    void (async () => {
      try {
        const me = await fetchJson<MeResponse>('/me');
        if (cancelled) return;
        setResolved({
          id: me.id,
          email: me.email,
          displayName: me.displayName,
          role: me.role,
        });
        setPhase('ready');
      } catch (cause) {
        if (cancelled) return;
        if (cause instanceof ApiError && cause.status === 401) {
          setPhase('redirecting');
          window.location.assign(loginUrl());
          return;
        }
        if (cause instanceof ApiError && cause.status === 403) {
          setPhase('denied');
          setMessage(cause.message);
          return;
        }
        setPhase('failed');
        setMessage(
          cause instanceof ApiError
            ? cause.message
            : 'The dashboard could not confirm who you are.'
        );
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [seeded]);

  const signOut = useCallback(() => {
    void (async () => {
      let target = '/';
      try {
        // The BFF revokes the session row and clears the cookie, then tells us
        // where Microsoft's sign-out lives. Ending only our session would
        // leave the next person at this machine one click from signing
        // straight back in.
        const body = await postJson<{ signOutUrl: string }>('/api/auth/logout');
        target = body.signOutUrl;
      } catch {
        // A failed logout must still drop the local view of the session.
      }
      window.location.assign(target);
    })();
  }, []);

  const value = useMemo<AuthState>(
    () => ({
      user: resolved,
      isResolved: phase !== 'resolving' && phase !== 'redirecting',
      isEditor: resolved?.role === 'editor',
      phase,
      message,
      signOut,
    }),
    [resolved, phase, message, signOut]
  );

  return (
    <AuthContext.Provider value={value}>
      {phase === 'ready' ? children : <AuthGate phase={phase} message={message} />}
    </AuthContext.Provider>
  );
}

/**
 * The pre-dashboard screen. Deliberately plain: it renders before any data
 * has loaded, and it is the first thing a member of leadership sees if
 * something is wrong, so it says what happened and what to do rather than
 * showing a spinner forever.
 */
function AuthGate({ phase, message }: { phase: AuthPhase; message: string }) {
  const heading =
    phase === 'denied' ? 'No access to this dashboard' : phase === 'failed' ? 'Sign-in failed' : '';

  return (
    <div
      className="flex min-h-screen items-center justify-center bg-chrome px-6 text-text-on-dark"
      role="status"
      aria-live="polite"
    >
      <div className="max-w-md text-center">
        <p className="text-md font-extrabold tracking-wider">
          JCORP <span className="text-accent-on-dark">AI</span> · EXECUTION
        </p>
        {heading === '' ? (
          <p className="mt-4 text-sm text-text-on-dark-muted">
            {phase === 'redirecting' ? 'Redirecting to Microsoft sign-in…' : 'Signing you in…'}
          </p>
        ) : (
          <>
            <h1 className="mt-4 text-lg font-bold">{heading}</h1>
            <p className="mt-2 text-sm text-text-on-dark-muted">{message}</p>
            {/* A link, not a fetch: /api/auth/login answers with a redirect to
                Microsoft, which only a top-level navigation can follow. */}
            <a
              href="/api/auth/login"
              className="mt-5 inline-block rounded bg-accent px-4 py-2 text-xs font-bold tracking-wide text-text-on-dark"
            >
              TRY AGAIN
            </a>
          </>
        )}
      </div>
    </div>
  );
}
